import React, { SetStateAction, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'radash';
import {
  defaultState,
  formTranscriptContextData,
  getNewCacheSuffix,
  getUsedSpeakers,
  initTranscript,
  setNewCacheSuffix
} from './TranscriptContextUtils';
import { TranscriptContextData } from './TranscriptContextTypes';
import { useAppContext } from '../AppContext/AppContext';
import { useClipsContext } from '../ClipsContext/ClipsContext';
import { SpeakerWithDetails } from '@/player-config/src/types/speaker-types';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { updateSharedAPIWithId, updateSpeakerImageMap } from '@/stores/sharedAPI/sharedAPI';
import {
  currentClip,
  saveClipChanges,
  updateClipMetadataWithId,
  isLayoutsEnabledForPreRecordedEventsFeatureFlag
} from '@/stores/clip';
import { Error } from '@/domains/global';
import useAnalytics from '@/hooks/useAnalytics';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { fullTranscriptStore } from '@/stores/fullTranscriptStore';

export const TranscriptContext = createContext<TranscriptContextData>(defaultState);
// Will be removing all the transcript related logic from here to content metadata store.
// Only the chapters related data will be stored here.
export function TranscriptContextProvider({
  children,
  eventId,
  broadcastId,
  onErrorCallback,
  onLoadCompleted,
  shouldAssociateWithSharedAPI = false,
  parentMountTime,
  parentContext
}: {
  children: React.ReactNode;
  eventId?: string;
  broadcastId?: string;
  onErrorCallback?: (error: Error) => void;
  onLoadCompleted?: () => void;
  /**
   * Determines whether the transcript context data should be associated with the shared API.
   * When set to true, the transcript data will be updated in the shared API store and used for clip config updates
   */
  shouldAssociateWithSharedAPI?: boolean;
  parentMountTime?: React.MutableRefObject<number>;
  parentContext?: string;
}) {
  const [transcriptContextData, setTranscriptContextData] = useState<TranscriptContextData>(() => ({
    ...defaultState,
    isTranscriptInitialized: false
  }));
  const { logger } = useAppContext();
  const { trackTranscriptLoadTime } = useAnalytics();
  const { clipId } = useClipsContext();
  const [isTranscriptInitialized, setIsTranscriptInitialized] = useState(false);

  const loadTranscript = () => {
    useContentMetadataStore.getState().setTranscriptLoading({
      contentId: broadcastId!,
      isLoading: true,
      areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag()
    });
    setTranscriptContextData(data => ({ ...data }));

    /**
     * This ensures that fresh transcript is loaded when speaker merge or reset has been performed or trimming is being done.
     * In all other cases, the cacheSuffix is not updated and transcript is loaded from disk cache.
     */
    const cacheSuffix = getNewCacheSuffix();

    initTranscript(cacheSuffix, eventId!, broadcastId!)
      .then(data => {
        const [transcriptResult, sessionSpeakers, chapterData] = data;
        if (transcriptResult.fullTranscript.length > 0) {
          fullTranscriptStore.update(() => transcriptResult.fullTranscript);
        }
        const contextData = formTranscriptContextData({
          transcriptResult,
          sessionSpeakers,
          chapterData,
          clipId,
          // @ts-ignore
          broadcastId
        });
        setTranscriptContextData(contextData);
        useContentMetadataStore.getState().updateSpeakerDetails({
          contentId: broadcastId!,
          speakerDetails: contextData.speakersWithDetails,
          areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag()
        });
        useContentMetadataStore.getState().updateTranscript({
          contentId: broadcastId!,
          transcript: transcriptResult.fullTranscript,
          areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag()
        });
      })
      .catch(err => {
        logger.error(err);
        if (onErrorCallback) {
          onErrorCallback(err);
        }
      })
      .finally(() => {
        setTranscriptContextData(data => ({ ...data }));
        useContentMetadataStore.getState().setTranscriptLoading({
          contentId: broadcastId!,
          isLoading: false,
          areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag()
        });
        setIsTranscriptInitialized(true);
        onLoadCompleted?.();
      });
  };

  const updateSpeakers = ({
    updatedSpeakers,
    shouldUpdateClip = true,
    speakerKey,
    speakerImage
  }: {
    updatedSpeakers: Record<string, SpeakerWithDetails>;
    shouldUpdateClip?: boolean;
    speakerKey?: string;
    speakerImage?: HTMLImageElement;
  }) => {
    setTranscriptContextData((data: TranscriptContextData) => {
      const newUpdatedSpeakers = { ...updatedSpeakers };

      // Temp fix by AshwinBhatkal. Will revisit later with https://goldcast.atlassian.net/browse/CR-3972
      Object.keys(newUpdatedSpeakers).forEach(key => {
        newUpdatedSpeakers[key] = {
          ...newUpdatedSpeakers[key]
        };
      });

      return {
        ...data,
        speakersWithDetails: newUpdatedSpeakers,
        usedSpeakers: getUsedSpeakers(newUpdatedSpeakers)
      } as TranscriptContextData;
    });

    // Update useContentMetadataStore with the new speaker details
    if (broadcastId && !isEmpty(updatedSpeakers)) {
      useContentMetadataStore.getState().updateSpeakerDetails({
        contentId: broadcastId,
        speakerDetails: updatedSpeakers,
        areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag()
      });
    }

    const clip = currentClip.getSnapshot()[clipId];
    const isFullLengthVideo = clip ? !!clip.asset_metadata.is_full_length : false;

    if (shouldUpdateClip && clipId) {
      if (speakerKey && speakerImage) {
        updateSpeakerImageMap(clipId, speakerKey, speakerImage);
      }

      // previous implementation for full video clips never dealt with speaker changes
      if (isFullLengthVideo) {
        updateClipMetadataWithId(clipId, {});
      }

      // Required to update the config with the updated speaker information
      saveClipChanges({
        clipId,
        shouldGenerateThumbnail: true,
        shouldAddToHistory: false,
        isEditedState: currentClip.getSnapshot()[clipId].asset_metadata.is_edited
      });
    }
  };

  const updateChapters = (
    updatedChapterData: Pick<TranscriptContextData, 'chapterData' | 'chapterGenerationStatus' | 'chaptersLastUpdatedAt'>
  ) => {
    setTranscriptContextData((data: SetStateAction<TranscriptContextData> | TranscriptContextData) => {
      return {
        ...data,
        ...updatedChapterData
      } as TranscriptContextData;
    });
  };

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.SpeakersChanged, updateSpeakers);
    return () => {
      EventBus.off(CustomEvents.SpeakersChanged, eventListener);
    };
  }, []);

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.UpdateChapters, updateChapters);
    return () => {
      EventBus.off(CustomEvents.UpdateChapters, eventListener);
    };
  }, []);

  useEffect(() => {
    // TODO: @AshwinBhatkal - This logic can be removed on trimming and transcript not loaded over here
    setNewCacheSuffix(Math.random().toString());
    loadTranscript();
  }, []);

  useEffect(() => {
    if (shouldAssociateWithSharedAPI) {
      updateSharedAPIWithId(clipId, { transcriptStore: transcriptContextData });
    }
    if (!isEmpty(transcriptContextData.speakersWithDetails)) {
      useContentMetadataStore.getState().updateSpeakerDetails({
        contentId: broadcastId!,
        speakerDetails: transcriptContextData.speakersWithDetails,
        areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag()
      });
    }
  }, [shouldAssociateWithSharedAPI, transcriptContextData, broadcastId]);

  const transcriptContextValue = useMemo(() => {
    return {
      ...transcriptContextData,
      isTranscriptInitialized
    };
  }, [transcriptContextData, isTranscriptInitialized]);

  useEffect(() => {
    if (eventId && broadcastId && isTranscriptInitialized && parentMountTime && parentContext) {
      const timeTaken = performance.now() - parentMountTime.current;
      trackTranscriptLoadTime({
        eventId,
        broadcastId,
        context: parentContext,
        loadTime: timeTaken
      });
    }
  }, [isTranscriptInitialized, parentMountTime, eventId, broadcastId, parentContext, trackTranscriptLoadTime]);

  return <TranscriptContext.Provider value={transcriptContextValue}>{children}</TranscriptContext.Provider>;
}

export function useTranscriptContext() {
  return useContext(TranscriptContext);
}
